import { inject, Service, WritableSignal } from '@angular/core';
import { WebWorkerData, WebWorkerPaths, WebWorkerState } from '../../model/web-worker-state';
import { NGXLogger } from 'ngx-logger';

@Service()
export class WebWorkerService {
  #workers: Record<string, Worker | null> = {};
  #logger = inject(NGXLogger);

  #initWorker(path: string, workerFactory: () => Worker, signal?: WritableSignal<WebWorkerData | null>): Worker {
    if (this.#workers[path]) {
      this.close(path);
    }
    try {
      const worker = workerFactory();
      this.#workers[path] = worker;
      worker.onmessage = ({ data }) => {
        if (data.type && signal) {
          signal.set(data);
        }
      };
      worker.addEventListener('error', (error: ErrorEvent) => {
        this.#logger.error(`Worker Runtime Error: ${error.message} at ${error.filename} line: ${error.lineno}`);
      });
      worker.addEventListener('messageerror', (event: MessageEvent) => {
        this.#logger.error(`Worker message error: ${event.data}`);
      });
      return worker;
    } catch (e: unknown) {
      this.#logger.error(`Worker initialization failed for ${path}: ${(e as Error).message}`);
      return new Worker('');
    }
  }

  initCacheWorker(signal?: WritableSignal<WebWorkerData | null>): WebWorkerService {
    this.#initWorker(
      '../../worker/cache/cache.worker.ts',
      () =>
        new Worker(new URL('../../worker/cache/cache.worker.ts', import.meta.url), {
          type: 'module',
        }),
      signal,
    );
    return this;
  }

  initGithubWorker(signal?: WritableSignal<WebWorkerData | null>): Worker {
    return this.#initWorker(
      '../../worker/git/git.worker.ts',
      () =>
        new Worker(new URL('../../worker/git/git.worker.ts', import.meta.url), {
          type: 'module',
        }),
      signal,
    );
  }

  close(key?: string) {
    if (key) {
      this.#workers[key]?.terminate();
      this.#workers[key] = null;
      return;
    }
    Object.values(this.#workers).forEach((worker) => worker?.terminate());
    this.#workers = {};
  }

  cacheNdaDocument() {
    this.#workers[WebWorkerPaths.NDA_DOCUMENT]?.postMessage({ type: WebWorkerState.NDA_DOCUMENT });
  }
}
